/**
 * The project's input map: named actions (`jump`, `left`, `pause`) with each
 * player's keyboard keys and gamepad buttons, saved in project.json `input`.
 * Scripts and kits read actions by name, never keys, so a game's controls can
 * be changed in one place. Plugins declare the actions they read, with default
 * bindings; those join the project's own when the project leaves them out.
 */

/** One player's keys for an action. */
export interface InputBinding {
  /** Phaser key names, such as `A`, `SPACE`, `LEFT`, `ENTER`. */
  keys?: string[];
}

/**
 * A named action and its bindings. Gamepad buttons are the same for every
 * player, since each player has a pad of their own; keys are per player,
 * since players share one keyboard.
 */
export interface InputAction {
  /** What scripts ask for, such as `jump`. Unique in the map. */
  name: string;
  description?: string;
  /** Gamepad buttons by the names in {@link GAMEPAD_BUTTONS}, such as `A` or `DPAD_LEFT`, for every player's pad. */
  pad?: string[];
  /** Keys per player: the first is player 1's. */
  players?: InputBinding[];
}

/** project.json `input`. */
export interface InputMap {
  /** How many players the game has bindings for. Default: the most any action binds. */
  players?: number;
  /** In the order the editor lists them. */
  actions: InputAction[];
}

/**
 * Gamepad buttons and stick directions, by the W3C standard gamepad layout.
 * Names are positions, so `A` is the bottom face button on every pad.
 */
export const GAMEPAD_BUTTONS = [
  'A', 'B', 'X', 'Y', 'LB', 'RB', 'LT', 'RT', 'BACK', 'START', 'LS', 'RS',
  'DPAD_UP', 'DPAD_DOWN', 'DPAD_LEFT', 'DPAD_RIGHT',
  'LSTICK_UP', 'LSTICK_DOWN', 'LSTICK_LEFT', 'LSTICK_RIGHT',
  'RSTICK_UP', 'RSTICK_DOWN', 'RSTICK_LEFT', 'RSTICK_RIGHT',
] as const;
export type GamepadButton = (typeof GAMEPAD_BUTTONS)[number];

/** How the editor shows a gamepad button. */
export const GAMEPAD_LABELS: Record<GamepadButton, string> = {
  A: 'A', B: 'B', X: 'X', Y: 'Y', LB: 'LB', RB: 'RB', LT: 'LT', RT: 'RT', BACK: 'Back', START: 'Start', LS: 'Left stick press', RS: 'Right stick press',
  DPAD_UP: 'D-pad up', DPAD_DOWN: 'D-pad down', DPAD_LEFT: 'D-pad left', DPAD_RIGHT: 'D-pad right',
  LSTICK_UP: 'Left stick up', LSTICK_DOWN: 'Left stick down', LSTICK_LEFT: 'Left stick left', LSTICK_RIGHT: 'Left stick right',
  RSTICK_UP: 'Right stick up', RSTICK_DOWN: 'Right stick down', RSTICK_LEFT: 'Right stick left', RSTICK_RIGHT: 'Right stick right',
};

/** An action a plugin reads, with the bindings it gets when the project has none of its own. */
export interface DeclaredAction extends InputAction {
  /** The plugin that reads it. */
  plugin?: string;
}

/**
 * The map a game runs with: the project's actions, then each declared action
 * the project leaves out, with its default bindings. A project action of the
 * same name wins whole, so rebinding it in the editor replaces the defaults.
 */
export function resolveInputMap(map: InputMap | undefined, declared: readonly DeclaredAction[] = []): InputMap {
  const actions: InputAction[] = (map?.actions ?? []).map((a) => ({ ...a }));
  const names = new Set(actions.map((a) => a.name));
  for (const d of declared) {
    if (names.has(d.name)) continue;
    names.add(d.name);
    const { plugin: _plugin, ...action } = d;
    actions.push(action);
  }
  const out: InputMap = { actions };
  if (map?.players !== undefined) out.players = map.players;
  return out;
}

/** How many players the map has bindings for. */
export function inputPlayerCount(map: InputMap): number {
  return Math.max(map.players ?? 0, ...map.actions.map((a) => a.players?.length ?? 0), 1);
}

/** A player's keys and gamepad buttons for an action (player 1 is `1`), empty when it has none. */
export function bindingOf(map: InputMap, action: string, player: number): { keys: string[]; pad: string[] } {
  const a = map.actions.find((x) => x.name === action);
  return { keys: a?.players?.[player - 1]?.keys ?? [], pad: a?.pad ?? [] };
}

/** Keys more than one player uses, so the editor can flag them: one key can't move two players. */
export function sharedKeys(map: InputMap): { key: string; players: number[] }[] {
  const by = new Map<string, Set<number>>();
  for (const a of map.actions) {
    a.players?.forEach((b, i) => {
      for (const k of b.keys ?? []) {
        const set = by.get(k) ?? new Set<number>();
        set.add(i + 1);
        by.set(k, set);
      }
    });
  }
  return [...by].filter(([, s]) => s.size > 1).map(([key, s]) => ({ key, players: [...s].sort((a, b) => a - b) }));
}
